import type { Region } from "wavesurfer.js/dist/plugins/regions.js"

const HANDLE_WIDTH_PIXELS = 14
const GRIP_LINE_WIDTH_PIXELS = 3
const GRIP_BOX_WIDTH_PIXELS = 8
const GRIP_BOX_HEIGHT_PIXELS = 22
const LABEL_LANE_HEIGHT_PIXELS = 16

// Grips sit on the media track, not on the page, so they take the track's ink
// rather than the page's text colours.
const GRIP_BASE_COLOR = "var(--coval-media-ink)"
const GRIP_HOVER_COLOR = "var(--coval-media-ink-strong)"
const GRIP_BOX_COLOR = "var(--coval-media-ink-strong)"

const RESIZE_HANDLE_SELECTOR =
  '[part="region-handle-left"], [part="region-handle-right"], [part*="region-handle"], .ws-region-handle'

function createGrip(handle: HTMLElement): void {
  const grip = document.createElement("div")
  grip.setAttribute("data-grip", "")

  const lineTop = document.createElement("div")
  lineTop.setAttribute("data-grip-line-top", "")

  const box = document.createElement("div")
  box.setAttribute("data-grip-box", "")

  const lineBottom = document.createElement("div")
  lineBottom.setAttribute("data-grip-line-bottom", "")

  grip.append(lineTop, box, lineBottom)
  handle.appendChild(grip)

  Object.assign(grip.style, {
    position: "absolute",
    top: "0",
    bottom: "0",
    left: "50%",
    transform: "translateX(-50%)",
    width: `${Math.max(GRIP_BOX_WIDTH_PIXELS, GRIP_LINE_WIDTH_PIXELS)}px`,
    pointerEvents: "none",
  })
  Object.assign(box.style, {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: `${GRIP_BOX_WIDTH_PIXELS}px`,
    height: `${GRIP_BOX_HEIGHT_PIXELS}px`,
    border: "2px solid var(--grip-color)",
    borderRadius: "3px",
    boxSizing: "border-box",
    background: "var(--grip-fill)",
    transition: "border-color 120ms ease, background-color 120ms ease",
  })

  const lineStyles = {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    width: `${GRIP_LINE_WIDTH_PIXELS}px`,
    height: `calc(50% - ${GRIP_BOX_HEIGHT_PIXELS / 2}px)`,
    background: "var(--grip-color)",
    borderRadius: "2px",
    transition: "border-color 120ms ease",
  }
  Object.assign(lineTop.style, lineStyles, { top: "0" })
  Object.assign(lineBottom.style, lineStyles, { bottom: "0" })
}

function styleResizeHandle(handle: HTMLElement): void {
  const part = handle.getAttribute("part") ?? ""
  const outsetPixels = HANDLE_WIDTH_PIXELS / 2

  Object.assign(handle.style, {
    position: "absolute",
    top: "0",
    bottom: "0",
    width: `${HANDLE_WIDTH_PIXELS}px`,
    background: "transparent",
    border: "none",
    zIndex: "10",
    pointerEvents: "auto",
    overflow: "visible",
    transition: "filter 120ms ease",
  })

  if (part.includes("left")) {
    handle.style.left = "0"
    handle.style.transform = `translateX(-${outsetPixels}px)`
  } else if (part.includes("right")) {
    handle.style.right = "0"
    handle.style.transform = `translateX(${outsetPixels}px)`
  } else {
    handle.style.transform = ""
  }

  if (!handle.dataset.gripHoverBound) {
    handle.dataset.gripHoverBound = "1"
    handle.addEventListener("mouseenter", () => {
      handle.style.setProperty("--grip-color", GRIP_HOVER_COLOR)
      handle.style.setProperty("--grip-fill", GRIP_HOVER_COLOR)
    })
    handle.addEventListener("mouseleave", () => {
      handle.style.setProperty("--grip-color", GRIP_BASE_COLOR)
      handle.style.setProperty("--grip-fill", GRIP_BOX_COLOR)
    })
  }

  if (!handle.querySelector("[data-grip]")) {
    handle.style.setProperty("--grip-color", GRIP_BASE_COLOR)
    handle.style.setProperty("--grip-fill", GRIP_BOX_COLOR)
    createGrip(handle)
  }
}

export function styleAudioRegionResizeHandles(
  regions: readonly Region[],
): void {
  for (const region of regions) {
    if (!region.element) {
      continue
    }
    region.element.style.overflow = "visible"
    const handles = region.element.querySelectorAll<HTMLElement>(
      RESIZE_HANDLE_SELECTOR,
    )
    handles.forEach(styleResizeHandle)
  }
}

export function layoutAudioRegionLabels(
  regions: readonly Region[],
  {
    compact,
  }: {
    compact: boolean
  },
): void {
  const labeledRegions = regions
    .filter((region) => region.content instanceof HTMLElement)
    .sort((first, second) => first.start - second.start)

  for (const region of labeledRegions) {
    region.content!.style.marginTop = "0px"
  }

  const laneRightEdges: number[] = []
  for (const region of labeledRegions) {
    const rect = region.content!.getBoundingClientRect()
    if (rect.width === 0) {
      continue
    }
    let lane = laneRightEdges.findIndex((rightEdge) => rightEdge <= rect.left)
    if (lane === -1) {
      lane = laneRightEdges.length
      laneRightEdges.push(rect.right)
    } else {
      laneRightEdges[lane] = rect.right
    }
    region.content!.style.marginTop = `${lane * LABEL_LANE_HEIGHT_PIXELS}px`
  }

  for (const region of labeledRegions) {
    const label = region.content!
    Object.assign(label.style, {
      fontSize: "0.8rem",
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      lineHeight: "1",
      padding: "2px 4px",
      borderRadius: "3px",
      whiteSpace: "nowrap",
      backgroundColor: "var(--surface-default)",
      color: "var(--coval-text)",
      border: "1px solid var(--coval-border)",
    })

    if (label.parentElement && !compact) {
      label.parentElement.style.top = "0%"
      label.parentElement.style.height = "50%"
    }
  }
}
